// The "rate" state: heading + visit line, star tiles, and — once a star is
// chosen — the expanded form (comment with tips + hint row, topic chips,
// incentives notice, consent checkbox, "Just one last step" + submit,
// privacy line).
//
// Layout follows Trustpilot's evaluate page: the heading stays put when a
// star is picked, the tiles shrink from 46px to 40px, the privacy line moves
// from under the tiles to the very bottom, and the hint under the textarea
// only appears once the field has been touched.
//
// All state lives in portalRateExperience so Edit from the thank-you card can
// re-open this form pre-filled (`editing` swaps the heading + submit copy).

import CheckRounded from "@mui/icons-material/CheckRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import { makeStyles } from "@mui/styles";
import { useState } from "react";

import StarTiles from "~/shared/components/StarTiles.jsx";
import {
  RATING_COLORS,
  RATING_FONT_FAMILY,
  RATING_MOBILE_MEDIA_QUERY,
  RATING_TILE_FORM,
  RATING_TILE_PICK,
  VISUALLY_HIDDEN,
} from "~/shared/styles/ratingPalette";
import {
  NoticeBox,
  PillButton,
  RatingLink,
  RatingLinkButton,
} from "./ratingUi.jsx";
import {
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_LENGTH,
  canSubmitRating,
  commentHint,
  commentPlaceholderForStars,
  visitSummaryLine,
} from "./ratingUtils.js";

export const COMMENT_FIELD_ID = "rating-comment";
export const COMMENT_HINT_ID = "rating-comment-hint";
export const COMMENT_REQUIREMENT_ID = "rating-comment-requirement";
export const TIPS_BOX_ID = "rating-tips";
export const HONEYPOT_FIELD_ID = "rating-website";

export const RATE_HEADING = "How would you rate your experience?";
export const EDIT_HEADING = "Rate your recent experience";
export const COMMENT_LABEL = "Tell us more about your experience";
export const TIP_TOGGLE_LABEL = "Want a tip?";
export const RATING_TIPS = [
  "Say what went well — or what didn't.",
  "Mention how the medication and the care team worked for you.",
  "Keep it about your own visit; skip anything you'd rather not share.",
];
export const COMMENT_REQUIREMENT = `Please write at least ${COMMENT_MIN_LENGTH} characters.`;
export const CHIPS_LABEL = "What stood out?";
export const CONSENT_LABEL =
  "Kick may share my comments publicly, signed with my first name and last initial.";
export const INCENTIVES_NOTICE =
  "Kick never offers payment or perks in exchange for a rating.";
export const LAST_STEP_HEADING = "Just one last step";
export const LAST_STEP_SUBLINE =
  "Your rating goes straight to the Kick care team.";
export const SUBMIT_LABEL = "Submit rating";
export const SUBMIT_UPDATED_LABEL = "Submit updated review";
export const PRIVACY_LINE =
  "Your rating goes to the Kick team and is never posted without your permission.";

const SECTION_GAP = 32;

const useStyles = makeStyles({
  h1: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1.2,
    margin: "72px 0 8px",
    color: RATING_COLORS.textPrimary,
    [RATING_MOBILE_MEDIA_QUERY]: {
      marginTop: "112px",
    },
  },
  subtitle: {
    margin: "0 0 16px",
    fontSize: "16px",
    lineHeight: 1.5,
    color: RATING_COLORS.textSecondary,
  },
  stars: {
    margin: "0 0 40px",
    lineHeight: 0,
  },
  starsExpanded: {
    marginBottom: "36px",
  },
  privacy: {
    margin: 0,
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textSecondary,
  },
  // Sits after the action block: SECTION_GAP + 8 = 40px.
  privacyBottom: {
    marginTop: "8px",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: `${SECTION_GAP}px`,
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "8px",
  },
  sectionLabel: {
    display: "block",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
  },
  tipToggle: {
    flexShrink: 0,
    fontSize: "14px",
    lineHeight: "20px",
  },
  tipIcon: {
    fontSize: "18px",
  },
  tips: {
    margin: "0 0 8px",
    padding: "12px 12px 12px 28px",
    backgroundColor: RATING_COLORS.chipsPanel,
    borderRadius: "8px",
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textPrimary,
    "& li + li": {
      marginTop: "4px",
    },
  },
  textarea: {
    display: "block",
    width: "100%",
    minHeight: "186px",
    padding: "16px",
    border: `1px solid ${RATING_COLORS.borderTextarea}`,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: RATING_COLORS.textPrimary,
    fontFamily: "inherit",
    fontSize: "16px",
    lineHeight: 1.5,
    resize: "none",
    "&::placeholder": {
      color: RATING_COLORS.placeholder,
      opacity: 1,
    },
    // The border stays; focus adds an offset accent ring.
    "&:focus": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
  },
  srOnly: VISUALLY_HIDDEN,
  // Always mounted (aria-live) — the inner row only renders once touched.
  hintInner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textSecondary,
  },
  hintIcon: {
    fontSize: "16px",
    flexShrink: 0,
  },
  // 8px under the textarea (SECTION_GAP - 24), not a full section gap.
  chipsPanel: {
    marginTop: `${8 - SECTION_GAP}px`,
    backgroundColor: RATING_COLORS.chipsPanel,
    borderRadius: "8px",
    padding: "16px 16px 12px",
  },
  chipsLabel: {
    fontSize: "12px",
    lineHeight: "20px",
    color: RATING_COLORS.textSecondary,
    marginBottom: "8px",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 4px",
  },
  // 24px tall (Trustpilot's are 22, but ours are real toggles — WCAG 2.5.8).
  chip: {
    display: "inline-flex",
    alignItems: "center",
    height: "24px",
    padding: "0 9px",
    borderRadius: "4px",
    border: `1px solid ${RATING_COLORS.borderChip}`,
    backgroundColor: "transparent",
    color: RATING_COLORS.textPrimary,
    fontFamily: "inherit",
    fontSize: "14px",
    lineHeight: "22px",
    cursor: "pointer",
    transition: "background-color 120ms ease, color 120ms ease",
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
      backgroundColor: RATING_COLORS.chipFill,
    },
    "&:focus-visible": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
  },
  chipSelected: {
    backgroundColor: RATING_COLORS.textPrimary,
    borderColor: RATING_COLORS.textPrimary,
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: RATING_COLORS.textPrimary,
    },
  },
  checkBox: {
    display: "block",
    boxSizing: "border-box",
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    border: `1px solid ${RATING_COLORS.borderInput}`,
    backgroundColor: "#FFFFFF",
  },
  checkBoxChecked: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    border: `1px solid ${RATING_COLORS.accent}`,
    backgroundColor: RATING_COLORS.accentPaleBg,
    color: RATING_COLORS.accent,
    "& svg": {
      fontSize: "16px",
    },
  },
  // Off-screen but still in the DOM so bots fill it; humans never see it.
  honeypot: VISUALLY_HIDDEN,
  // 40px under the consent row (SECTION_GAP + 8).
  actions: {
    marginTop: "8px",
  },
  actionsHeading: {
    margin: "0 0 4px",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
  },
  actionsSubline: {
    margin: "0 0 20px",
    fontSize: "12px",
    lineHeight: "16px",
    color: RATING_COLORS.textPrimary,
  },
  error: {
    margin: "0 0 12px",
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.error,
    textAlign: "center",
  },
  submitRow: {
    display: "flex",
    justifyContent: "center",
  },
  submit: {
    minWidth: "324px",
    [RATING_MOBILE_MEDIA_QUERY]: {
      minWidth: 0,
    },
  },
  spinner: {
    color: "#FFFFFF",
  },
});

const checkboxSx = {
  padding: "2px 12px 2px 2px",
  "&.Mui-focusVisible": {
    outline: `2px solid ${RATING_COLORS.accent}`,
    outlineOffset: "-4px",
  },
};

const consentLabelSx = {
  alignItems: "flex-start",
  margin: 0,
  "& .MuiFormControlLabel-label": {
    fontFamily: RATING_FONT_FAMILY,
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textPrimary,
  },
};

function PrivacyLine({ className }) {
  return (
    <p className={className}>
      {PRIVACY_LINE}{" "}
      <RatingLink href="/privacy" target="_blank" rel="noopener noreferrer">
        Privacy Policy
      </RatingLink>
    </p>
  );
}

export default function RatingForm({
  invitation,
  values,
  onChange,
  onToggleTag,
  onSubmit,
  submitting = false,
  error = "",
  editing = false,
}) {
  const classes = useStyles();
  const [touched, setTouched] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  const tags = (invitation && invitation.tags) || [];
  const expanded = (Number(values.stars) || 0) >= 1;
  const hint = expanded && touched ? commentHint(values.comment) : null;
  const canSubmit = canSubmitRating(values);
  const selectedTags = values.tags || [];
  const tileSize = expanded ? RATING_TILE_FORM : RATING_TILE_PICK;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit && !submitting) {
          onSubmit();
        }
      }}
    >
      <h1 className={classes.h1}>{editing ? EDIT_HEADING : RATE_HEADING}</h1>
      <p className={classes.subtitle}>{visitSummaryLine(invitation)}</p>

      <div
        className={[classes.stars, expanded && classes.starsExpanded]
          .filter(Boolean)
          .join(" ")}
      >
        <StarTiles
          value={values.stars}
          onChange={(n) => onChange("stars", n)}
          size={tileSize}
          mobileSize={tileSize}
          name="stars"
          ariaLabel={RATE_HEADING}
        />
      </div>

      {expanded ? null : <PrivacyLine className={classes.privacy} />}

      {expanded ? (
        <div className={classes.details}>
          <div>
            <div className={classes.labelRow}>
              <label
                htmlFor={COMMENT_FIELD_ID}
                className={classes.sectionLabel}
              >
                {COMMENT_LABEL}
              </label>
              <RatingLinkButton
                className={classes.tipToggle}
                aria-expanded={tipsOpen ? "true" : "false"}
                aria-controls={TIPS_BOX_ID}
                onClick={() => setTipsOpen((open) => !open)}
              >
                <LightbulbOutlined
                  className={classes.tipIcon}
                  aria-hidden="true"
                />
                {TIP_TOGGLE_LABEL}
              </RatingLinkButton>
            </div>
            {tipsOpen ? (
              <ul id={TIPS_BOX_ID} className={classes.tips}>
                {RATING_TIPS.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            ) : null}
            <textarea
              id={COMMENT_FIELD_ID}
              name="comment"
              className={classes.textarea}
              placeholder={commentPlaceholderForStars(values.stars)}
              maxLength={COMMENT_MAX_LENGTH}
              rows={6}
              value={values.comment}
              aria-describedby={`${COMMENT_REQUIREMENT_ID} ${COMMENT_HINT_ID}`}
              onBlur={() => setTouched(true)}
              onChange={(e) => {
                setTouched(true);
                onChange("comment", e.target.value);
              }}
            />
            {/* Screen readers hear the requirement on focus; sighted users get
                the hint row below once the field has been touched. */}
            <span id={COMMENT_REQUIREMENT_ID} className={classes.srOnly}>
              {COMMENT_REQUIREMENT}
            </span>
            <div id={COMMENT_HINT_ID} aria-live="polite">
              {hint ? (
                <div className={classes.hintInner}>
                  <InfoOutlined
                    className={classes.hintIcon}
                    aria-hidden="true"
                  />
                  <span>{hint.text}</span>
                </div>
              ) : null}
            </div>
          </div>

          {tags.length > 0 ? (
            <div className={classes.chipsPanel}>
              <div className={classes.chipsLabel} id="rating-tags-label">
                {CHIPS_LABEL}
              </div>
              <div
                className={classes.chips}
                role="group"
                aria-labelledby="rating-tags-label"
              >
                {tags.map((tag) => {
                  const selected = selectedTags.includes(tag.code);
                  return (
                    <button
                      type="button"
                      key={tag.code}
                      aria-pressed={selected ? "true" : "false"}
                      className={[
                        classes.chip,
                        selected && classes.chipSelected,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => onToggleTag(tag.code)}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <NoticeBox compact>{INCENTIVES_NOTICE}</NoticeBox>

          <FormControlLabel
            sx={consentLabelSx}
            control={
              <Checkbox
                sx={checkboxSx}
                disableRipple
                name="consentToPublish"
                checked={!!values.consentToPublish}
                onChange={(e) => onChange("consentToPublish", e.target.checked)}
                icon={<span className={classes.checkBox} aria-hidden="true" />}
                checkedIcon={
                  <span className={classes.checkBoxChecked} aria-hidden="true">
                    <CheckRounded />
                  </span>
                }
              />
            }
            label={CONSENT_LABEL}
          />

          {/* Honeypot: bots fill every text input; the server drops the
              submission when this one is non-empty. */}
          <div className={classes.honeypot} aria-hidden="true">
            <label htmlFor={HONEYPOT_FIELD_ID}>Website</label>
            <input
              id={HONEYPOT_FIELD_ID}
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              value={values.website || ""}
              onChange={(e) => onChange("website", e.target.value)}
            />
          </div>

          <div className={classes.actions}>
            <p className={classes.actionsHeading}>{LAST_STEP_HEADING}</p>
            <p className={classes.actionsSubline}>{LAST_STEP_SUBLINE}</p>
            {error ? (
              <div role="alert" className={classes.error}>
                {error}
              </div>
            ) : null}
            <div className={classes.submitRow}>
              <PillButton
                type="submit"
                className={classes.submit}
                disabled={!canSubmit || submitting}
                busy={submitting}
              >
                {submitting ? (
                  <CircularProgress
                    size={20}
                    className={classes.spinner}
                    aria-label="Submitting"
                  />
                ) : editing ? (
                  SUBMIT_UPDATED_LABEL
                ) : (
                  SUBMIT_LABEL
                )}
              </PillButton>
            </div>
          </div>

          <PrivacyLine
            className={[classes.privacy, classes.privacyBottom].join(" ")}
          />
        </div>
      ) : null}
    </form>
  );
}
